import PhoneNumber from 'awesome-phonenumber';
import toTitleCase from 'titlecase';
import ParseAddress from 'parse-address';
import DataService from './data-service';
import moment from 'moment';
import type { StashCategory } from '@stash/shared-types';

const NotImplementedError = class NotImplementedError extends Error {};
void NotImplementedError;

const csv_import_column_names = {
  COMPANY_NAME: 'Company',
  STASHER: 'Stasher',
  STASHERS_USING: "Stasher's Using",
  CATEGORY: 'Category',
  SUB_CATEGORY: 'Sub-Category',
  CONTACT_NAME: 'Contact Name',
  BUSINESS_PHONE: 'Business Phone',
  WEBSITE_URL: 'Website',
  BUSINESS_EMAIL: 'Business Email',
  ADDRESS_STREET: 'Address Street',
  ADDRESS_CITY: 'Address City',
  ADDRESS_STATE_CODE: 'State Code',
  ADDRESS_ZIP: 'Address Zip',
  NOTES: 'Notes',
};

interface CsvRow {
  [key: string]: string;
}

interface StasherName {
  firstName: string;
  lastName: string | null;
}

interface CategoryItem {
  name: string;
}

interface SubCategoryItem {
  name: string;
  parentName: string | null;
}

interface VendorAddress {
  street: string | null;
  street2?: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string;
}

interface ExtractedVendor {
  companyName: string | null;
  stasherName: string | null;
  stashersUsing: string | null;
  categoryName: string | null;
  subCategoryName: string | null;
  contactName: string | null;
  businessPhone: string | null;
  websiteUrl: string | null;
  businessEmail: string | null;
  address: VendorAddress | null;
  notes: string | null;
  sourceOfRecordName: string;
}

interface StasherImportData {
  firstName: string | null;
  lastName: string | null;
  email?: string | null;
  mobilePhone?: string | null;
}

interface CleanedStasher {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobilePhone: string | null;
}

interface CleanStashersResult {
  stashers: CleanedStasher[];
  report: string[];
}

interface ImportReport {
  insertCount: number;
  updateCount: number;
  issues: string[];
}

interface ImportResult {
  categories: unknown[];
  stashers: unknown[];
  vendors: unknown[];
  stashCards: unknown[];
}

interface ImportState {
  categories: Array<{ id: unknown; name: string }>;
  stashers: Array<{ id: unknown; firstName: string; lastName: string | null; email?: string | null; mobilePhone?: string | null }>;
  subCategories?: Array<{ id: unknown; name: string }>;
  vendors?: unknown[];
}

class ImportDataManager {
  _toTitleCase(str: string | null): string | null {
    return str ? toTitleCase(str.trim().toLowerCase()) : null;
  }

  _valueOrNullIfEmptyForKey(key: string, hash: CsvRow): string | null {
    const str = hash[key];
    return str && str.length > 0 ? str.trim() : null;
  }

  _emailIsValid(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  extractStashers(importData: CsvRow[]): StasherName[] {
    const allStashers: Array<string | null> = importData.map((item) =>
      this._valueOrNullIfEmptyForKey(csv_import_column_names.STASHER, item),
    );

    importData
      .map((item) => this._valueOrNullIfEmptyForKey(csv_import_column_names.STASHERS_USING, item))
      .forEach((fieldValue) => {
        if (fieldValue) {
          fieldValue
            .split(',')
            .forEach((stasherName) => {
              if (stasherName.trim().length > 0) {
                allStashers.push(stasherName.trim());
              }
            });
        }
      });

    return allStashers
      .filter(
        (item, index, array) => item && item.length > 0 && array.indexOf(item) === index,
      )
      .map((name) => {
        const nameParts = (name as string).trim().split(' ');
        return {
          firstName: nameParts[0],
          lastName: nameParts.length > 1 ? nameParts[1] : null,
        };
      });
  }

  extractCategories(importData: CsvRow[]): CategoryItem[] {
    return importData
      .map((item) =>
        this._toTitleCase(this._valueOrNullIfEmptyForKey(csv_import_column_names.CATEGORY, item)),
      )
      .filter((item, index, array) => item && item.length > 0 && array.indexOf(item) === index)
      .map((item) => ({ name: item as string }));
  }

  extractSubCategories(importData: CsvRow[]): SubCategoryItem[] {
    return importData
      .map((item) => {
        return JSON.stringify({
          name: this._toTitleCase(
            this._valueOrNullIfEmptyForKey(csv_import_column_names.SUB_CATEGORY, item),
          ),
          parentName: this._toTitleCase(
            this._valueOrNullIfEmptyForKey(csv_import_column_names.CATEGORY, item),
          ),
        });
      })
      .filter((item, index, array) => item && array.indexOf(item) === index)
      .map((s) => JSON.parse(s) as SubCategoryItem)
      .filter((item) => item.name !== null);
  }

  extractVendors(importData: CsvRow[]): ExtractedVendor[] {
    const cicn = csv_import_column_names;
    const timeStampString = moment.utc().format();
    const sourceOfRecordName = `CSV Import ${timeStampString}`;

    return importData
      .map((item) => {
        let businessPhone: string | null = null;
        const phoneString = this._valueOrNullIfEmptyForKey(cicn.BUSINESS_PHONE, item);
        if (phoneString) {
          const pn = new PhoneNumber(phoneString, 'US');
          if (pn.isValid()) {
            businessPhone = pn.getNumber('national');
          } else {
            console.log(
              'business phone "' +
                phoneString +
                '" invalid for vendor ' +
                item[cicn.COMPANY_NAME] +
                ' ' +
                item[cicn.CONTACT_NAME],
            );
          }
        }

        let address: VendorAddress | null = {
          street: this._valueOrNullIfEmptyForKey(cicn.ADDRESS_STREET, item),
          city: this._valueOrNullIfEmptyForKey(cicn.ADDRESS_CITY, item),
          region: this._valueOrNullIfEmptyForKey(cicn.ADDRESS_STATE_CODE, item),
          postalCode: this._valueOrNullIfEmptyForKey(cicn.ADDRESS_ZIP, item),
          // Spreadsheet import is US-only today; countryCode is required on the wire.
          countryCode: 'USA',
        };

        if (!address.postalCode) {
          const parsedAddress = address.street
            ? ParseAddress.parseLocation(address.street)
            : null;
          if (!parsedAddress) {
            address = null;
          } else {
            const street = parsedAddress.prefix
              ? `${parsedAddress.number} ${parsedAddress.prefix} ${parsedAddress.street} ${parsedAddress.type}`
              : `${parsedAddress.number} ${parsedAddress.street} ${parsedAddress.type}`;

            if (parsedAddress.zip) {
              address = {
                street,
                street2: parsedAddress.sec_unit_num
                  ? `${parsedAddress.sec_unit_type} ${parsedAddress.sec_unit_num}`
                  : null,
                city: parsedAddress.city || null,
                region: parsedAddress.state || null,
                postalCode: parsedAddress.zip,
                countryCode: 'USA',
              };
            }
          }
        }

        if (!address?.street || !address?.postalCode) {
          address = null;
        }

        return {
          companyName: this._valueOrNullIfEmptyForKey(cicn.COMPANY_NAME, item),
          stasherName: this._valueOrNullIfEmptyForKey(cicn.STASHER, item),
          stashersUsing: this._valueOrNullIfEmptyForKey(cicn.STASHERS_USING, item),
          categoryName: this._toTitleCase(
            this._valueOrNullIfEmptyForKey(cicn.CATEGORY, item),
          ),
          subCategoryName: this._toTitleCase(
            this._valueOrNullIfEmptyForKey(cicn.SUB_CATEGORY, item),
          ),
          contactName: this._valueOrNullIfEmptyForKey(cicn.CONTACT_NAME, item),
          businessPhone,
          websiteUrl: this._valueOrNullIfEmptyForKey(cicn.WEBSITE_URL, item),
          businessEmail: this._valueOrNullIfEmptyForKey(cicn.BUSINESS_EMAIL, item),
          address,
          notes: this._valueOrNullIfEmptyForKey(cicn.NOTES, item),
          sourceOfRecordName,
        };
      })
      .filter((vendor) => vendor.companyName || vendor.contactName);
  }

  dedupVendorsWithMerge(vendors: ExtractedVendor[]): ExtractedVendor[] {
    const vendorsMatch = (v1: ExtractedVendor, v2: ExtractedVendor): boolean => {
      const sameName =
        (v1.companyName != null && v1.companyName === v2.companyName) ||
        (v1.contactName != null && v1.contactName === v2.contactName);
      const sameAddress =
        !v1.address ||
        !v2.address ||
        (v1.address.street === v2.address.street &&
          v1.address.postalCode === v2.address.postalCode);
      return sameName && sameAddress;
    };

    const mergePreferValueToNull = (
      obj1: ExtractedVendor,
      obj2: ExtractedVendor,
    ): ExtractedVendor => {
      const answer = { ...obj1 };
      for (const key in obj2) {
        const k = key as keyof ExtractedVendor;
        if (obj2[k] !== null) {
          (answer as Record<string, unknown>)[key] = obj2[k];
        }
      }
      return answer;
    };

    return vendors.reduce<ExtractedVendor[]>((deduped, vendor) => {
      const idx = deduped.findIndex((item) => vendorsMatch(item, vendor));
      if (idx > -1) {
        let stashersUsing = '';
        if (deduped[idx].stashersUsing) stashersUsing = deduped[idx].stashersUsing as string;
        if (vendor.stashersUsing) {
          if (stashersUsing.length > 0) stashersUsing += ', ';
          stashersUsing += vendor.stashersUsing;
        }
        deduped[idx] = mergePreferValueToNull(deduped[idx], vendor);
        deduped[idx].stashersUsing = stashersUsing;
        return deduped;
      } else {
        return [...deduped, vendor];
      }
    }, []);
  }

  cleanStashersWithReport(importData: StasherImportData[]): CleanStashersResult {
    const report: string[] = [];
    const notEmptyVal = (str: string | null | undefined): boolean => {
      return !!(str && str.trim().length > 0);
    };

    const importDataSansEmpties = importData.filter((item) => {
      return (
        notEmptyVal(item.firstName) ||
        notEmptyVal(item.lastName) ||
        notEmptyVal(item.email) ||
        notEmptyVal(item.mobilePhone)
      );
    });

    const stashers: CleanedStasher[] = importDataSansEmpties.map((item) => {
      let mobilePhone: string | null = null;
      let email: string | null = null;

      if (item.mobilePhone) {
        const pn = new PhoneNumber(item.mobilePhone, 'US');
        if (pn.isValid()) {
          mobilePhone = pn.getNumber('national');
        } else {
          const reportLine = `mobilePhone (${item.mobilePhone}) invalid for ${item.firstName} ${item.lastName}`;
          console.log(reportLine);
          report.push(reportLine);
        }
      }

      if (item.email) {
        const trimmedEmail = item.email.trim();
        if (trimmedEmail.length > 0) {
          if (this._emailIsValid(trimmedEmail)) {
            email = trimmedEmail;
          } else {
            const reportLine = `email (${trimmedEmail}) invalid for ${item.firstName} ${item.lastName}`;
            console.log(reportLine);
            report.push(reportLine);
          }
        }
      }

      if (!(email || mobilePhone)) {
        if (item.firstName || item.lastName) {
          const reportLine = `both email and mobile missing for ${item.firstName} ${item.lastName}`;
          console.log(reportLine);
          report.push(reportLine);
        }
      }

      return {
        firstName: this._toTitleCase(item.firstName || null),
        lastName: this._toTitleCase(item.lastName || null),
        email,
        mobilePhone,
      };
    });

    return { stashers, report };
  }

  publishImportStashersData(importData: StasherImportData[]): Promise<ImportReport> {
    const dataService = new DataService();
    const { stashers, report } = this.cleanStashersWithReport(importData);

    return (dataService.getStashers() as Promise<ImportState['stashers']>).then(
      (existingStashers) => {
        const updateStashers: Array<CleanedStasher & { id: unknown }> = [];
        const insertStashers: CleanedStasher[] = [];

        stashers.forEach((importStasher) => {
          const match = existingStashers.find((existingStasher) => {
            return (
              (importStasher.email &&
                existingStasher.email &&
                importStasher.email.toLowerCase() ===
                  existingStasher.email.toLowerCase()) ||
              (importStasher.mobilePhone &&
                importStasher.mobilePhone === existingStasher.mobilePhone) ||
              (importStasher.firstName &&
                importStasher.lastName &&
                importStasher.firstName === existingStasher.firstName &&
                importStasher.lastName === existingStasher.lastName)
            );
          });

          if (match) {
            updateStashers.push({ ...importStasher, id: match.id });
          } else {
            insertStashers.push(importStasher);
          }
        });

        const allUpdates = updateStashers.map((stasher) =>
          dataService.updateStasher(stasher as unknown as Record<string, unknown>),
        );

        return Promise.all(allUpdates)
          .then((updates) => {
            return Promise.all([dataService.saveNewStashers(insertStashers), updates]);
          })
          .then(([newStashers, updates]) => {
            return {
              insertCount: (newStashers as unknown[]).length,
              updateCount: (updates as unknown[]).length,
              issues: report,
            };
          });
      },
    );
  }

  publishImportData(importData: CsvRow[]): Promise<ImportResult> {
    return new Promise((resolve, reject) => {
      const dataService = new DataService();

      const categories = this.extractCategories(importData);
      const subCategories = this.extractSubCategories(importData);
      const stashers = this.extractStashers(importData);
      const vendors = this.dedupVendorsWithMerge(this.extractVendors(importData));

      const categoryNames = categories.map((cat) => cat.name);
      Promise.all([
        dataService.getCategories(categoryNames) as Promise<Array<{ id: unknown; name: string }>>,
        dataService.getStashers() as Promise<ImportState['stashers']>,
      ])
        .then(([categoryMatches, allStashers]) => {
          const newCategories = categories.filter(
            (cat) => categoryMatches.findIndex((m) => m.name === cat.name) < 0,
          );
          const newStashers = stashers.filter(
            (stasher) =>
              allStashers.findIndex(
                (m) =>
                  (m.email && m.email === stasher.firstName) ||
                  (m.firstName === stasher.firstName && m.lastName === stasher.lastName),
              ) < 0,
          );
          return Promise.all([
            dataService.saveCategories(newCategories as unknown as StashCategory[]) as Promise<Array<{ id: unknown; name: string }>>,
            dataService.saveNewStashers(newStashers) as Promise<ImportState['stashers']>,
            categoryMatches,
            allStashers,
          ]);
        })
        .then(([newCategories, newStashers, currentCategories, currentStashers]) => {
          const importState: ImportState = {
            categories: [...currentCategories, ...newCategories],
            stashers: [...currentStashers, ...newStashers],
          };
          const subCatNames = subCategories.map((cat) => cat.name);
          return Promise.all([
            dataService.getCategories(subCatNames) as Promise<Array<{ id: unknown; name: string }>>,
            importState,
          ] as const);
        })
        .then(([subCategoryMatches, importState]) => {
          importState.subCategories = subCategoryMatches;

          const newSubCategories = subCategories.filter(
            (cat) => subCategoryMatches.findIndex((m) => m.name === cat.name) < 0,
          );
          const subCats = newSubCategories.map((subCat) => {
            const parent = importState.categories.find((cat) => cat.name === subCat.parentName);
            if (!parent) {
              console.log(
                'failed to find parent category named "' +
                  subCat.parentName +
                  '" for sub-category "' +
                  subCat.name +
                  '"',
              );
              return { name: subCat.name };
            }
            return { name: subCat.name, parentCategoryId: parent.id };
          });

          return Promise.all([
            dataService.saveCategories(subCats as unknown as StashCategory[]) as Promise<Array<{ id: unknown; name: string }>>,
            importState,
          ] as const);
        })
        .then(([newSubCategories, importState]) => {
          importState.categories = [
            ...importState.categories,
            ...(importState.subCategories || []),
            ...newSubCategories,
          ];
          delete importState.subCategories;

          const assignedVendors = vendors.map((vendor) => {
            const vendorCopy: Record<string, unknown> = { ...vendor };
            let categoryIds: unknown[] = [];
            let categoryName: string | null | undefined;
            try {
              categoryName = vendor.subCategoryName || vendor.categoryName;
              const category = importState.categories.filter(
                (cat) => cat.name === categoryName,
              )[0];
              categoryIds.push(category.id);
              vendorCopy['categoryIds'] = categoryIds;
            } catch (err) {
              console.log(
                'failed to find sub-category named "' +
                  categoryName +
                  '" for vendor "' +
                  vendor.companyName +
                  '"',
                err,
              );
            }

            const fullNameMatchesStasherName = (
              fullName: string | null,
              stasher: ImportState['stashers'][number],
            ): boolean => {
              if (!fullName) return false;
              if (stasher.lastName) {
                return fullName === stasher.firstName + ' ' + stasher.lastName;
              }
              return fullName === stasher.firstName;
            };

            vendorCopy['stasherIds'] = [];

            const stasher = importState.stashers.find((s) =>
              fullNameMatchesStasherName(vendor.stasherName, s),
            );
            if (stasher) {
              vendorCopy['originalStasherId'] = stasher.id;
              (vendorCopy['stasherIds'] as unknown[]).push(stasher.id);
            } else {
              console.log(
                'failed to find stasher named "' +
                  vendor.stasherName +
                  '" for vendor "' +
                  vendor.companyName +
                  '"',
              );
            }

            if (vendor.stashersUsing) {
              vendor.stashersUsing.split(',').forEach((stasherName) => {
                if (stasherName.trim().length > 0) {
                  const s = importState.stashers.find((st) =>
                    fullNameMatchesStasherName(stasherName.trim(), st),
                  );
                  if (s) {
                    (vendorCopy['stasherIds'] as unknown[]).push(s.id);
                  } else {
                    console.log(
                      'failed to find stasher using named "' +
                        stasherName +
                        '" for vendor "' +
                        vendor.companyName +
                        '"',
                    );
                  }
                }
              });
            }

            vendorCopy['stasherIds'] = (vendorCopy['stasherIds'] as unknown[]).filter(
              (item, index, array) => item && array.indexOf(item) === index,
            );

            delete vendorCopy['subCategoryName'];
            delete vendorCopy['categoryName'];
            delete vendorCopy['stasherName'];
            delete vendorCopy['stashersUsing'];
            return vendorCopy;
          });

          return Promise.all([
            dataService.saveVendors(assignedVendors) as Promise<
              Array<{
                id: unknown;
                companyName?: string;
                stashcards?: Array<{ id: unknown; stasher: { id: unknown } }>;
              }>
            >,
            importState,
          ] as const);
        })
        .then(([savedVendors, importState]) => {
          importState.vendors = savedVendors;

          const stashCards = savedVendors.reduce<
            Array<{ id: unknown; stasherId: unknown; vendorId: unknown }>
          >((accumulator, vendor) => {
            let vendorStashCards: Array<{ id: unknown; stasherId: unknown; vendorId: unknown }> =
              [];
            if (!vendor.stashcards) {
              console.log('WARNING: ' + vendor.companyName + ' has no stashCards');
            } else {
              vendorStashCards = vendor.stashcards.map((stashCard) => ({
                id: stashCard.id,
                stasherId: stashCard.stasher.id,
                vendorId: vendor.id,
              }));
            }
            return [...accumulator, ...vendorStashCards];
          }, []);

          resolve({
            categories: importState.categories,
            stashers: importState.stashers,
            vendors: importState.vendors,
            stashCards,
          });
        })
        .catch(reject);
    });
  }
}

export default ImportDataManager;
